import React, { useState, useEffect } from 'react';
import { X, DollarSign } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { useToast } from './ui/use-toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const OfferBuilderModal = ({ clientId, clientName, onClose, onSuccess }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    funding_amount: '',
    rate: '1.20',
    payback_amount: '',
    payment_frequency: 'daily',
    term_length: '',
    payment_amount: '',
    offer_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [manuallyEdited, setManuallyEdited] = useState({
    payback: false,
    payment: false
  });

  // Calculate payback amount
  const calculatePayback = () => {
    const funding = parseFloat(formData.funding_amount) || 0;
    const rate = parseFloat(formData.rate) || 1.20;
    return funding * rate;
  };

  // Calculate payment amount
  const calculatePayment = () => {
    const payback = parseFloat(formData.payback_amount) || 0;
    const term = parseInt(formData.term_length) || 1;
    return payback / term;
  };

  // Auto-update payback when funding or rate changes (unless manually edited)
  useEffect(() => {
    if (!manuallyEdited.payback && formData.funding_amount && formData.rate) {
      const funding = parseFloat(formData.funding_amount);
      const rate = parseFloat(formData.rate);
      if (!isNaN(funding) && !isNaN(rate)) {
        const calculated = funding * rate;
        setFormData(prev => ({ ...prev, payback_amount: String(calculated) }));
      }
    }
  }, [formData.funding_amount, formData.rate, manuallyEdited.payback]);

  // Auto-update payment when payback or term changes (unless manually edited)
  useEffect(() => {
    if (!manuallyEdited.payment && formData.payback_amount && formData.term_length) {
      const payback = parseFloat(formData.payback_amount);
      const term = parseInt(formData.term_length);
      if (!isNaN(payback) && !isNaN(term) && term > 0) {
        const calculated = payback / term;
        setFormData(prev => ({ ...prev, payment_amount: calculated.toFixed(2) }));
      }
    }
  }, [formData.payback_amount, formData.term_length, manuallyEdited.payment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.funding_amount || !formData.payback_amount || !formData.term_length || !formData.payment_amount) {
      toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.post(`${API_BASE}/clients/${clientId}/offers`, {
        business_name: clientName,
        funding_amount: parseFloat(formData.funding_amount),
        rate: parseFloat(formData.rate),
        payback_amount: parseFloat(formData.payback_amount),
        term_length: parseInt(formData.term_length),
        payment_frequency: formData.payment_frequency,
        payment_amount: parseFloat(formData.payment_amount),
        funding_date: formData.offer_date,
        notes: formData.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({ title: 'Success', description: 'Offer created and moved to Submitted Deals!' });
      onSuccess();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create offer', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const getFrequencyLabel = () => {
    return formData.payment_frequency === 'daily' ? 'Days' : 'Weeks';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Create Offer - {clientName}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Funding Amount and Rate */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Funding Amount *</label>
              <Input
                type="number"
                step="0.01"
                required
                value={formData.funding_amount}
                onChange={(e) => setFormData({...formData, funding_amount: e.target.value})}
                placeholder="$50,000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Rate (Factor) *</label>
              <Input
                type="number"
                step="0.01"
                required
                value={formData.rate}
                onChange={(e) => setFormData({...formData, rate: e.target.value})}
                placeholder="1.20"
              />
              <p className="text-xs text-gray-500 mt-1">Common: 1.15 - 1.50</p>
            </div>
          </div>

          {/* Payback Amount */}
          <div>
            <label className="block text-sm font-medium mb-2">Payback Amount *</label>
            <Input
              type="number"
              step="0.01"
              required
              value={formData.payback_amount}
              onChange={(e) => {
                setFormData({...formData, payback_amount: e.target.value});
                setManuallyEdited(prev => ({ ...prev, payback: true }));
              }}
              onFocus={() => setManuallyEdited(prev => ({ ...prev, payback: true }))}
              placeholder="Auto-calculated"
            />
            <p className="text-xs text-gray-500 mt-1">
              Auto: ${calculatePayback().toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} (Funding × Rate)
            </p>
          </div>

          {/* Frequency Toggle */}
          <div>
            <label className="block text-sm font-medium mb-3">Payment Frequency *</label>
            <div className="flex gap-3">
              <button
                type="button"
                disabled={loading}
                onClick={() => setFormData({...formData, payment_frequency: 'daily'})}
                className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition ${
                  formData.payment_frequency === 'daily'
                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                📅 Daily
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={() => setFormData({...formData, payment_frequency: 'weekly'})}
                className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition ${
                  formData.payment_frequency === 'weekly'
                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                📆 Weekly
              </button>
            </div>
          </div>

          {/* Term Length */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Term Length (# of {getFrequencyLabel()}) *
              </label>
              <Input
                type="number"
                required
                value={formData.term_length}
                onChange={(e) => setFormData({...formData, term_length: e.target.value})}
                placeholder={formData.payment_frequency === 'daily' ? '110 days' : '12 weeks'}
              />
              <p className="text-xs text-gray-500 mt-1">
                How many {formData.payment_frequency === 'daily' ? 'business days' : 'weeks'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Payment Amount *</label>
              <Input
                type="number"
                step="0.01"
                required
                value={formData.payment_amount}
                onChange={(e) => {
                  setFormData({...formData, payment_amount: e.target.value});
                  setManuallyEdited(prev => ({ ...prev, payment: true }));
                }}
                onFocus={() => setManuallyEdited(prev => ({ ...prev, payment: true }))}
                placeholder="Auto-calculated"
              />
              <p className="text-xs text-gray-500 mt-1">
                Auto: ${calculatePayment().toFixed(2)} (Payback ÷ Term)
              </p>
            </div>
          </div>

          {/* Offer Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Offer Date</label>
              <Input
                type="date"
                value={formData.offer_date}
                onChange={(e) => setFormData({...formData, offer_date: e.target.value})}
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium mb-2">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="w-full border rounded p-3 h-24"
              placeholder="Additional offer details or conditions..."
            />
          </div>

          {/* Offer Summary */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-5">
            <h4 className="font-bold text-lg mb-3 text-blue-900">📊 Offer Summary</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded p-3">
                <p className="text-xs text-gray-600 mb-1">Funding Amount</p>
                <p className="font-bold text-xl text-gray-900">
                  ${(parseFloat(formData.funding_amount) || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </p>
              </div>
              <div className="bg-white rounded p-3">
                <p className="text-xs text-gray-600 mb-1">Payback Amount</p>
                <p className="font-bold text-xl text-green-600">
                  ${(parseFloat(formData.payback_amount) || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </p>
              </div>
              <div className="bg-white rounded p-3">
                <p className="text-xs text-gray-600 mb-1">Term</p>
                <p className="font-bold text-xl text-gray-900">
                  {formData.term_length || '0'} {getFrequencyLabel()}
                </p>
              </div>
              <div className="bg-white rounded p-3">
                <p className="text-xs text-gray-600 mb-1">Payment Amount</p>
                <p className="font-bold text-xl text-orange-600">
                  ${(parseFloat(formData.payment_amount) || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <Button type="submit" disabled={loading} className="flex-1 bg-orange-600 hover:bg-orange-700 py-3 text-base">
              <DollarSign size={18} className="mr-2" />
              {loading ? 'Creating Offer...' : 'Create Offer & Submit for Underwriting'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="px-8" disabled={loading}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OfferBuilderModal;
